'use client';
import React from 'react';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import OverlayImage from './_OverlayImage';
import Product from '@models/product';
import { ProductContext } from '@context/ProductProvider';
import { ImageProps } from '@repo/ui/src/types/image';
import { classNames } from '@repo/ui/src/utils/classNames';
import Image from 'next/image';

interface ProductOverlayProps {
  product?: Product;
  aspectWidth?: number;
  aspectHeight?: number;
  imageWidth: number;
  className?: string;
  display: boolean;
  hideBaseImage?: boolean; // If an image is checked don't show base image
  images?: Array<{ checked: boolean; overlayImage: ImageProps }>;
}

const ProductOverlay = ({
  product,
  images: _images,
  className = '',
  aspectWidth,
  aspectHeight,
  imageWidth,
  display,
  hideBaseImage = false,
}: ProductOverlayProps) => {
  const imageUrl = buildImageSrc(
    product?.frontFacingImages?.closedImage,
    imageWidth,
    aspectWidth,
    aspectHeight,
  );
  const { productList } = React.useContext(ProductContext);
  const products = productList.filter((p) => p.isAddon);
  const images: Array<{ checked: boolean; overlayImage: any }> = _images?.length
    ? (_images as any)
    : products.map((p) => ({ checked: p.checked, overlayImage: p.overlayImage }));
  const imageChecked = images.some((img: any) => img.checked);
  const hideBase = imageChecked && hideBaseImage;

  return (
    <div
      className={classNames(
        'product-overlay relative w-full h-full z-[2] transition-opacity transition-500 ease-linear pointer-events-none flex items-center',
        display ? 'opacity-100' : 'opacity-0',
        className,
      )}
    >
      {product?.frontFacingImages?.closedImage && (
        <Image
          className={classNames(
            'absolute h-full w-full object-contain transition-opacity transition-500 ease-linear pointer-events-none',
            hideBase ? 'opacity-0' : 'opacity-100',
          )}
          src={imageUrl}
          fill
          alt={product.mainImage?.accessibilityLabel || product.name || 'Product Image'}
        />
      )}
      {images.map(
        (image: any, index: number) =>
          image?.overlayImage && (
            <OverlayImage
              key={image.overlayImage?.name ?? image.overlayImage?.accessibilityLabel ?? index}
              image={image.overlayImage}
              display={image.checked}
              aspectHeight={aspectHeight}
              aspectWidth={aspectWidth}
              imageWidth={imageWidth}
            />
          ),
      )}
    </div>
  );
};

export default ProductOverlay;
